import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  adminReviewFormListSchema,
  adminReviewListSchema,
  type AdminReviewFormList,
  type AdminReviewList,
} from '../schemas';
import { useToken } from './use-token';

export type AdminReviewSort = 'recent' | 'oldest' | 'best' | 'worst';

export type AdminReviewFormSort = 'name' | 'reviews' | 'best' | 'worst';

export type AdminReviewsParams = {
  page?: number;
  limit?: number;
  cabinetId?: string;
  sort?: AdminReviewSort;
  search?: string;
};

/** Avis de tous les cabinets (ou d'un seul), pour le back-office. */
export function useAdminReviews({
  page = 1,
  limit = 20,
  cabinetId = '',
  sort = 'recent',
  search = '',
}: AdminReviewsParams = {}) {
  const api = useApi();
  const token = useToken();

  const query = new URLSearchParams({ page: String(page), limit: String(limit), sort });
  if (cabinetId) query.set('cabinetId', cabinetId);
  if (search) query.set('search', search);

  return useQuery({
    queryKey: queryKeys.adminReviews.list({ page, limit, cabinetId, sort, search }),
    queryFn: () =>
      api.get<AdminReviewList>(`/admin/reviews?${query}`, { schema: adminReviewListSchema }),
    enabled: token !== null,
    placeholderData: keepPreviousData,
  });
}

/** Formulaire d'avis public de chaque cabinet, avec son lien. */
export function useAdminReviewForms({
  page = 1,
  limit = 20,
  search = '',
  sort = 'name',
}: { page?: number; limit?: number; search?: string; sort?: AdminReviewFormSort } = {}) {
  const api = useApi();
  const token = useToken();

  const query = new URLSearchParams({ page: String(page), limit: String(limit), sort });
  if (search) query.set('search', search);

  return useQuery({
    queryKey: queryKeys.adminReviews.forms({ page, limit, search, sort }),
    queryFn: () =>
      api.get<AdminReviewFormList>(`/admin/reviews/forms?${query}`, {
        schema: adminReviewFormListSchema,
      }),
    enabled: token !== null,
    placeholderData: keepPreviousData,
  });
}
